import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { history, store as reduxStore } from './index'
import { logoutUser } from './redux/user/actions'
import { showErrorNotification } from './redux/notifications/actions'
import { getApiErrorMessage } from './utils/apiError'
import { getCookie } from './api/cookie'
import store from 'store'

declare module 'axios' {
  interface AxiosRequestConfig {
    // Skips the global error toast when the caller shows the failure itself.
    silent?: boolean
  }
}

const { REACT_APP_API_ENDPOINT, REACT_APP_CUSTOM_NODE_ENV } = process.env

if (!REACT_APP_API_ENDPOINT) {
  const errorMessage = `Missing REACT_APP_API_ENDPOINT in .env.${REACT_APP_CUSTOM_NODE_ENV} file \n Add and restart server`
  alert(errorMessage)
  throw errorMessage
}

export const getApiRootUrl = (): string => (
  REACT_APP_CUSTOM_NODE_ENV === 'staging'
    ? store.get('channel') || REACT_APP_API_ENDPOINT
    : REACT_APP_API_ENDPOINT
)

/** @deprecated Use getApiRootUrl() — kept for download links that build URLs manually */
export const baseURL = getApiRootUrl()

axios.defaults.withCredentials = true

const instance = axios.create({
  withCredentials: true,
})

const onRequestSuccess = (config: AxiosRequestConfig) => {
  config.baseURL = `${getApiRootUrl()}/api`
  const token = getCookie('csrftoken')
  if (token) {
    config.headers['X-CSRFToken'] = token
  }
  return config
}

const onRequestFail = (error: AxiosError) => {
  return Promise.reject(error)
}

instance.interceptors.request.use(onRequestSuccess, onRequestFail)

const onResponseSuccess = (response: AxiosResponse) => {
  return response
}

const onResponseFail = (error: AxiosError) => {
  let shouldLogOut = false
  const isSilent = !!error?.config?.silent
  if (error && !error.response) {
    if (!isSilent) {
      reduxStore.dispatch(showErrorNotification('Cant connect to the server. Please try again later'))
    }
  } else {
    const { status } = error.response || {}
    if (status === 401) {
      shouldLogOut = true
    }
    if (status === 403) {
      const isUserAuthorized = reduxStore.getState()?.user.authorized
      if (isUserAuthorized) history.replace('/403')
    }
    const notification = getApiErrorMessage(error, '')
    // Suppress global toast for JSON 404s (e.g. "User not found") — calling code handles these.
    // Only show for HTML 404s (missing endpoints, parsed as a raw string body) and all other statuses.
    const isJsonNotFound = status === 404 && typeof error.response?.data !== 'string'
    if (notification && status !== 403 && !isJsonNotFound && !isSilent) {
      reduxStore.dispatch(showErrorNotification(notification))
    }
  }
  if (shouldLogOut) {
    reduxStore.dispatch(logoutUser(false))
  }
  return Promise.reject(error)
}
instance.interceptors.response.use(onResponseSuccess, onResponseFail)

export default instance
